import batchTeacherStructureService from 'ApiService/Teacher/batch.teacher.service';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const SelectClass = ({
  setDisable,
  setLoading,
  classData,
  setClassData,
  courses,
  loadingCourses,
  clearData,
}) => {
  const [batchStructures, setBatchStructures] = useState([]);
  const [loadingStructures, setLoadingStructures] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [errors, setErrors] = useState([]);
  const { state } = useContext(AuthContext);
  const selectedBranch = state?.user?.branch?._id || state?.user?.branch?.name;

  const [batches, setBatches] = useState([]);

  const handleCourseChange = async (event) => {
    const courseId = event.target.value;

    setClassData({
      courseId,
      structureId: '',
      batchId: '',
    });

    setBatchStructures([]);
    setBatches([]);
    clearData();

    if (!courseId) return;

    try {
      setLoadingStructures(true);

      const res = await batchTeacherStructureService.getBatchStructuresByCourse(
        courseId
      );

      if (res.data.success) {
        setBatchStructures(res.data.batchStructures || []);
      }
    } catch (err) {
      toast.error('Failed to load structures');
    } finally {
      setLoadingStructures(false);
    }
  };



  useEffect(() => {
    const newErrors = [];
    if (
      !classData?.courseId ||
      !classData?.structureId ||
      !classData?.batchId
    ) {
      newErrors.push('Please fill in all fields.');
    }
    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [classData, setDisable]);

  return (
    <div className="w-full rounded-lg px-3 pt-7 md:px-8">
      {/* Header */}
      <div className="flex justify-center">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Select Class Information
        </h4>
      </div>

      {/* Course Select */}
      <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-center md:gap-6">
        <div className="w-full md:w-1/2">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Course
          </label>
          <select
            onChange={handleCourseChange}
            value={classData?.courseId}
            name="course"
            className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Click here to Select Course</option>
            {loadingCourses ? (
              <option>Loading courses...</option>
            ) : courses.length === 0 ? (
              <option className="cursor-pointer font-bold text-brand-500">
                No courses available
              </option>
            ) : (
              courses.map((course, i) => (
                <option key={course._id} value={course._id}>
                  {i + 1}. {course.courseName}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="w-full md:w-1/2">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Structure
          </label>

          <select
            value={classData.structureId}
            onChange={async (e) => {
              const structureId = e.target.value;

              setClassData({
                ...classData,
                structureId,
                batchId: '',
              });

              setBatches([]);
              clearData();

              if (!structureId) return;

              try {
                setLoadingBatches(true);

                const res =
                  await batchTeacherStructureService.getBatchesByCourseStructure(
                    classData.courseId,
                    structureId,
                    selectedBranch
                  );

                if (res.data.success) {
                  setBatches(res.data.batches || []);
                }
              } catch (err) {
                toast.error('Failed to load batches');
              } finally {
                setLoadingBatches(false);
              }
            }}
            className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Select Structure</option>

            {loadingStructures ? (
              <option className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white">
                Loading...
              </option>
            ) : (
              batchStructures.map((bs) => (
                <option
                  key={bs._id}
                  value={bs._id}
                  className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                >
                  {bs.name}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Batch Select */}
        <div className="w-full md:w-1/2">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Batch
          </label>
          <select
            value={classData.batchId}
            onChange={(e) => {
              const selectedBatch = batches.find(
                (b) => b._id === e.target.value
              );
              setClassData({
                ...classData,
                batchId: e.target.value,
                batchStartDate: selectedBatch?.startDate || null,
              });
              clearData();
            }}
            className="mt-2 w-full md:min-w-[400px] rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Select Batch</option>

            {loadingBatches ? (
              <option>Loading...</option>
            ) : batches.length > 0 ? (
              batches.map((b) => {
                const batchDisplay = `B${b.batchNumber}`;
                
                const date = new Date(b.startDate);
                const day = date.getDate().toString().padStart(2, '0');
                const month = date.toLocaleString('default', { month: 'short' });
                const year = date.getFullYear().toString().slice(-2);
                const dateDisplay = `${day}${month}${year}`;

                const dayMap = {
                  Monday: 'M',
                  Tuesday: 'T',
                  Wednesday: 'W',
                  Thursday: 'Th',
                  Friday: 'F',
                  Saturday: 'S',
                  Sunday: 'Su',
                };

                const scheduleDisplay = b.daySchedules
                  ?.map((schedule) => {
                    const dayShort = dayMap[schedule.day] || schedule.day;
                    const time = schedule.startTime.replace(':00', '').replace(' ', '');
                    return `${dayShort}-${time}`;
                  })
                  .join(' | ');

                const branchInitials = b.branches
                  ?.map((br) => br.name.split(',').pop().trim()[0])
                  .join('');

                const schedulePart = scheduleDisplay ? `(${scheduleDisplay} )` : '';
                const fullDisplay = `${batchDisplay} | ${dateDisplay} ${schedulePart} ${branchInitials || ''}`.trim();

                return (
                  <option
                    key={b._id}
                    value={b._id}
                    className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                  >
                    {fullDisplay}
                  </option>
                );
              })
            ) : (
              <option>No batches available</option>
            )}
          </select>
        </div>
      </div>
      {/* Display errors */}
      {errors.length > 0 && (
        <div className="text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectClass;
